const getChildPermissions = (items) => {
  // First, collect all permissions from all nested levels
  const getAllPermissions = (items) => {
    return items.reduce((permissions, item) => {
      if (item.requiredPermissions) {
        permissions.push(...item.requiredPermissions);
      }
      if (item.items) {
        permissions.push(...getAllPermissions(item.items));
      }
      return permissions;
    }, []);
  };

  // Get all unique permissions
  const allPermissions = [...new Set(getAllPermissions(items))];

  // If there are no permissions at all (unrestricted items), return empty array
  if (allPermissions.length === 0) {
    return [];
  }

  // Return all possible permissions - if user has ANY of these, the section should be visible
  return allPermissions;
};

export const navigationItems = [
  {
    id: 'organizational-structure',
    translationKey: 'nav.organizationalStructure.main',
    type: 'dropdown',

    items: [
      {
        id: 'companies',
        translationKey: 'nav.organizationalStructure.companies',
        path: '/organizational-structure/companies',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_COMPANIES_BRANCHES']
      },
      {
        id: 'structure-levels',
        translationKey: 'nav.organizationalStructure.structureLevels',
        path: '/structure-levels',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_GROUP_LEVELS']
      },
      {
        id: 'positions',
        translationKey: 'nav.organizationalStructure.positions',
        path: '/organizational-structure/positions',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_POSITIONS']
      },
      {
        id: 'structure-and-positions',
        translationKey: 'nav.organizationalStructure.structureAndPositions',
        path: '/structure-and-positions',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_POSITIONS_STRUCTURE']
      },
      {
        id: 'job-descriptions',
        translationKey: 'nav.organizationalStructure.jobDescriptions',
        path: '/job-descriptions',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_JOB_DESCRIPTIONS']
      },
      {
        id: 'locations',
        translationKey: 'nav.organizationalStructure.locations',
        path: '/locations',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_LOCATIONS']
      },
      {
        id: 'document-signers',
        translationKey: 'nav.organizationalStructure.documentSigners',
        path: '/document-signers',
        type: 'item-header',
        requiredPermissions: ['ORG_STRUCT_SIGNATORIES_DOCS']
      },
      {
        id: 'nomenclatures',
        translationKey: 'nav.organizationalStructure.nomenclatures',
        path: '/nomenclatures-oz',
        type: 'item-header',

      }
    ]
  },
  {
    id: 'personnel',
    translationKey: 'nav.personnel.main',
    type: 'dropdown',
    items: [
      {
        id: 'personnel-settings',
        translationKey: 'nav.personnel.settings.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'labor-office-nra',
            translationKey: 'nav.personnel.settings.laborOfficeNra.main',
            type: 'item-dropdown',
            items: [
              {
                id: 'employment-contracts',
                translationKey: 'nav.personnel.settings.laborOfficeNra.employmentContracts',
                path: '/personnel/settings/labor-office-nra/employment-contracts',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_LABOR_CONTRACTS']
              },
              {
                id: 'additional-agreements',
                translationKey: 'nav.personnel.settings.laborOfficeNra.additionalAgreements',
                path: '/personnel/settings/labor-office-nra/additional-agreements',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_ADD_AGREEMENTS']
              },
              {
                id: 'termination-employment',
                translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.main',
                type: 'item-dropdown',

                items: [
                  {
                    id: 'termination-basis',
                    translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.terminationBasis',
                    path: '/personnel/settings/labor-office-nra/termination/basis',
                    type: 'nested-item',
                    requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_TERMINATION_GROUND']
                  },
                  {
                    id: 'notice-periods',
                    translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.noticePeriods',
                    path: '/personnel/settings/labor-office-nra/termination/notice-periods',
                    type: 'nested-item',
                    requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_TERMINATION_NOTICE_PERIODS']
                  },
                  {
                    id: 'compensation',
                    translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.compensation.main',
                    type: 'item-dropdown',
                    items: [
                      {
                        id: 'employer-compensation',
                        translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.compensation.employer',
                        path: '/personnel/settings/labor-office-nra/termination/compensation/employer',
                        type: 'nested-item',
                        requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_TERMINATION_COMPENSATIONS_BY_EMPLOYER']
                      },
                      {
                        id: 'employee-compensation',
                        translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.compensation.employee',
                        path: '/personnel/settings/labor-office-nra/termination/compensation/employee',
                        type: 'nested-item',
                        requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_TERMINATION_COMPENSATIONS_BY_EMPLOYEE']
                      }
                    ],
                  },
                  {
                    id: 'resignation-reasons',
                    translationKey: 'nav.personnel.settings.laborOfficeNra.terminationOfEmployment.resignationReasons',
                    path: '/personnel/settings/labor-office-nra/termination/resignation-reasons',
                    type: 'item',
                    requiredPermissions: ['PERSONNEL_LABOR_CODE_NAP_TERMINATION_RESIGNATION_REASONS']
                  }
                ],
              }
            ],
          },
          {
            id: 'educational-institutions',
            translationKey: 'nav.personnel.settings.educationalInstitutions',
            path: '/personnel/settings/educational-institutions',
            type: 'item',
            requiredPermissions: ['PERSONNEL_EDUCATIONAL_INSTITUTIONS']
          },
          {
            id: 'education-qualification',
            translationKey: 'nav.personnel.settings.educationQualification',
            path: '/personnel/settings/education-qualification',
            type: 'item',
            requiredPermissions: ['PERSONNEL_EDU_QUALIFICATION_LEVEL']
          },
          {
            id: 'employee-lists',
            translationKey: 'nav.personnel.settings.employeeLists',
            path: '/personnel/settings/employee-lists',
            type: 'item',
            requiredPermissions: ['PERSONNEL_EMPLOYEE_LISTS']
          }
        ]
      },
      {
        id: 'hr-register',
        translationKey: 'nav.personnel.hrRegister',
        path: '/staff/hr-register',
        type: 'item-header',
      },
      {
        id: 'employment-contracts',
        translationKey: 'nav.personnel.employmentContracts.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'register',
            translationKey: 'nav.personnel.employmentContracts.register',
            path: '/personnel/employment-contracts/register',
            type: 'item',
            requiredPermissions: ['PERSONNEL_LABOR_CONTRACTS']
          },
          {
            id: 'reports',
            translationKey: 'nav.personnel.employmentContracts.reports.main',
            type: 'item-dropdown',
            items: [
              {
                id: 'personal-files',
                translationKey: 'nav.personnel.employmentContracts.reports.personalFiles',
                path: '/personnel/employment-contracts/reports/personal-files',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_PERSONAL_FILES']
              },
              {
                id: 'hr-information',
                translationKey: 'nav.personnel.employmentContracts.reports.hrInformation',
                path: '/personnel/employment-contracts/reports/hr-information',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_HR_INFO']
              },
              {
                id: 'hr-movement',
                translationKey: 'nav.personnel.employmentContracts.reports.hrMovement',
                path: '/personnel/employment-contracts/reports/hr-movement',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_HR_MOVEMENT']
              },
              {
                id: 'hr-management',
                translationKey: 'nav.personnel.employmentContracts.reports.hrManagement',
                path: '/personnel/employment-contracts/reports/hr-management',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_HR_MGMT']
              },
              {
                id: 'personnel-photos',
                translationKey: 'nav.personnel.employmentContracts.reports.personnelPhotos',
                path: '/personnel/employment-contracts/reports/personnel-photos',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_PHOTOS']
              },
              {
                id: 'dsk-reports',
                translationKey: 'nav.personnel.employmentContracts.reports.dskReports',
                path: '/personnel/employment-contracts/reports/dsk',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_REPORTS_DSK']
              }
            ],
          },
          {
            id: 'employer-change',
            translationKey: 'nav.personnel.employmentContracts.employerChange.main',
            type: 'item-dropdown',
            items: [
              {
                id: 'labor-code-123',
                translationKey: 'nav.personnel.employmentContracts.employerChange.laborCode123',
                path: '/employment-compensations/settings/regulatory-documents/art-123',
                type: 'nested-item',
                requiredPermissions: ['PERSONNEL_EMPLOYEES_CHANGE_EMPLOYER']
              }
            ],
          }
        ],
      },
      {
        id: 'photo-assignment',
        translationKey: 'nav.personnel.photoAssignment',
        path: '/personnel/photo-assignment',
        type: 'item-header',
      },
      {
        id: 'self-service',
        translationKey: 'nav.personnel.selfService.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'reports',
            translationKey: 'nav.personnel.selfService.reports',
            path: '/personnel/self-service/reports',
            type: 'item',
          },
          {
            id: 'leave-requests',
            translationKey: 'nav.personnel.selfService.leaveRequests',
            path: '/personnel/self-service/leave-requests',
            type: 'item',
          },
          {
            id: 'substitute',
            translationKey: 'nav.personnel.selfService.substitute',
            path: '/personnel/self-service/substitute',
            type: 'item',
          },
          {
            id: 'doctor-info',
            translationKey: 'nav.personnel.selfService.doctorInfo',
            path: '/personnel/self-service/doctor-info',
            type: 'item',
          },
          {
            id: 'emergency-info',
            translationKey: 'nav.personnel.selfService.emergencyInfo',
            path: '/personnel/self-service/emergency-info',
            type: 'item',
          },
          {
            id: 'annual-assessment',
            translationKey: 'nav.personnel.selfService.annualAssessment',
            path: '/personnel/self-service/annual-assessment',
            type: 'item',
          },
          {
            id: 'annual-goals',
            translationKey: 'nav.personnel.selfService.annualGoals',
            path: '/personnel/self-service/annual-goals',
            type: 'item',
          },
          {
            id: 'remote-work-documents',
            translationKey: 'nav.personnel.selfService.remoteWorkDocuments',
            path: '/personnel/self-service/remote-work-documents',
            type: 'item',
          },
          {
            id: 'remote-work-request',
            translationKey: 'nav.personnel.selfService.remoteWorkRequest',
            path: '/personnel/self-service/remote-work-request',
            type: 'item',
          }
        ],
      },
      {
        id: 'management',
        translationKey: 'nav.personnel.management.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'staff-contracts-report',
            translationKey: 'nav.personnel.management.staffContractsReport',
            path: '/personnel/management/staff-contracts-report',
            type: 'item',
          },
          {
            id: 'previous-positions',
            translationKey: 'nav.personnel.management.previousPositions',
            path: '/personnel/management/previous-positions',
            type: 'item',
          },
          {
            id: 'annual-assessment-prep',
            translationKey: 'nav.personnel.management.annualAssessmentPrep',
            path: '/personnel/management/annual-assessment-prep',
            type: 'item',
          },
          {
            id: 'annual-goals-setting',
            translationKey: 'nav.personnel.management.annualGoalsSetting',
            path: '/personnel/management/annual-goals-setting',
            type: 'item',
          },
          {
            id: 'hire-change-application',
            translationKey: 'nav.personnel.management.hireChangeApplication',
            path: '/personnel/management/hire-change-application',
            type: 'item',
          },
          {
            id: 'home-office-confirmation',
            translationKey: 'nav.personnel.management.homeOfficeConfirmation',
            path: '/personnel/management/home-office-confirmation',
            type: 'item',
          },
          {
            id: 'extra-hours-order',
            translationKey: 'nav.personnel.management.extraHoursOrder',
            path: '/personnel/management/extra-hours-order',
            type: 'item',
          }
        ],
      },
      {
        id: 'hr-bp-form-approval',
        translationKey: 'nav.personnel.hrBpFormApproval',
        path: '/personnel/hr-bp-form-approval',
        type: 'item-header',
      },
      {
        id: 'first-form-approval',
        translationKey: 'nav.personnel.firstFormApproval',
        path: '/personnel/first-form-approval',
        type: 'item-header',
      },
      {
        id: 'hr-administration',
        translationKey: 'nav.personnel.hrAdministration',
        path: '/personnel/hr-administration',
        type: 'item-header',
      },
      {
        id: 'remote-work-approval',
        translationKey: 'nav.personnel.remoteWorkApproval',
        path: '/personnel/remote-work-approval',
        type: 'item-header',
      },
      {
        id: 'overtime-order-approval',
        translationKey: 'nav.personnel.overtimeOrderApproval',
        path: '/personnel/overtime-order-approval',
        type: 'item-header',
      }
    ],
  },
  {
    id: 'absences',
    translationKey: 'nav.absences.main',
    type: 'dropdown',
    items: [
      {
        id: 'absences-section',
        translationKey: 'nav.absences.absencesSection.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'absence-types',
            translationKey: 'nav.absences.absencesSection.types',
            path: '/absences/types',
            type: 'item',
            requiredPermissions: ['ABSENCES_TYPES']
          },
          {
            id: 'personal-calendars',
            translationKey: 'nav.absences.absencesSection.personalCalendars',
            path: '/absences/personal-calendars',
            type: 'item',
            requiredPermissions: ['ABSENCES_PERSONAL_CALENDARS']
          },
          {
            id: 'hr-leave-approvals',
            translationKey: 'nav.absences.absencesSection.hrLeaveApprovals',
            path: '/absences/hr-leave-approvals',
            type: 'item',
          },
          {
            id: 'paid-leave-approval',
            translationKey: 'nav.absences.absencesSection.paidLeaveApproval',
            path: '/absences/paid-leave-approval',
            type: 'item',
            requiredPermissions: ['ABSENCES_APPROVAL_PAID_LEAVE']
          },
          {
            id: 'provision-accrual',
            translationKey: 'nav.absences.absencesSection.provisionAccrual',
            path: '/absences/provision-accrual',
            type: 'item',
            requiredPermissions: ['ABSENCES_ACCRUAL_PROVISIONS']
          }
        ],
      },
      {
        id: 'management',
        translationKey: 'nav.absences.management.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'leave-approval',
            translationKey: 'nav.absences.management.leaveApproval',
            path: '/absences/management/leave-approval',
            type: 'item',
          },
          {
            id: 'reports',
            translationKey: 'nav.absences.management.reports',
            path: '/absences/management/reports',
            type: 'item',
          }
        ],
      },
      {
        id: 'reports',
        translationKey: 'nav.absences.reports.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'absences',
            translationKey: 'nav.absences.reports.absences',
            path: '/absences/reports/absences',
            type: 'item',
            requiredPermissions: ['ABSENCES_REPORTS_ABSENCES']
          },
          {
            id: 'long-term-absences',
            translationKey: 'nav.absences.reports.longTermAbsences',
            path: '/absences/reports/long-term-absences',
            type: 'item',
            requiredPermissions: ['ABSENCES_REPORTS_LONG_TERM_TITULARS']
          },
          {
            id: 'late-requests',
            translationKey: 'nav.absences.reports.lateRequests',
            path: '/absences/reports/late-requests',
            type: 'item',
            requiredPermissions: ['ABSENCES_REPORTS_LATE_REQUESTS']
          },
          {
            id: 'attendance-calendar',
            translationKey: 'nav.absences.reports.attendanceCalendar',
            path: '/absences/reports/attendance-calendar',
            type: 'item',
            requiredPermissions: ['ABSENCES_REPORTS_ATTENDANCE_CALENDAR']
          },
          {
            id: 'attendance-form',
            translationKey: 'nav.absences.reports.attendanceForm',
            path: '/absences/reports/attendance-form',
            type: 'item',
            requiredPermissions: ['ABSENCES_REPORTS_FORM_76']
          }
        ],
      }
    ],
  },
  {
    id: 'business-trips',
    translationKey: 'nav.businessTrips.main',
    type: 'dropdown',
    items: [
      {
        id: 'trip-type',
        translationKey: 'nav.businessTrips.businessTripsSection.tripType',
        path: '/business-trips/trip-type',
        type: 'item-header',
        requiredPermissions: ['BUSINESS_TRIPS_TYPE']
      },
      {
        id: 'trip-request',
        translationKey: 'nav.businessTrips.businessTripsSection.tripRequest',
        path: '/business-trips/trip-request',
        type: 'item-header',
      },
      {
        id: 'accounting-approval',
        translationKey: 'nav.businessTrips.businessTripsSection.accountingApproval',
        path: '/business-trips/accounting-approval',
        type: 'item-header',
      },
      {
        id: 'manager-approval',
        translationKey: 'nav.businessTrips.businessTripsSection.managerApproval',
        path: '/business-trips/manager-approval',
        type: 'item-header',
      },
      {
        id: 'hr-approval',
        translationKey: 'nav.businessTrips.businessTripsSection.hrApproval',
        path: '/business-trips/hr-approval',
        type: 'item-header',
      },
      {
        id: 'reports',
        translationKey: 'nav.businessTrips.businessTripsSection.reports',
        path: '/business-trips/reports',
        type: 'item-header',
        requiredPermissions: ['BUSINESS_TRIPS_REPORTS']
      }
      // 
    ],
  },
  {
    id: 'schedules-and-shifts',
    translationKey: 'nav.schedulesAndShifts.main',
    type: 'dropdown',
    items: [
      {
        id: 'management',
        translationKey: 'nav.schedulesAndShifts.management.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'schedule-entry',
            translationKey: 'nav.schedulesAndShifts.management.scheduleEntry',
            path: '/schedules-and-shifts/management/schedule-entry',
            type: 'item',
          },
          {
            id: 'overtime-entry',
            translationKey: 'nav.schedulesAndShifts.management.overtimeEntry',
            path: '/schedules-and-shifts/management/overtime-entry',
            type: 'item',
          }
        ],
      },
      {
        id: 'shift-settings',
        translationKey: 'nav.schedulesAndShifts.othersSection.shiftSettings',
        path: '/schedules-and-shifts/shift-settings',
        type: 'item-header',
        requiredPermissions: ['SCHEDULES_SHIFTS_SETTINGS']
      },
      {
        id: 'hr-overtime-approval',
        translationKey: 'nav.schedulesAndShifts.othersSection.hrOvertimeApproval',
        path: '/schedules-and-shifts/hr-overtime-approval',
        type: 'item-header',
      },
      {
        id: 'hr-schedule-approval',
        translationKey: 'nav.schedulesAndShifts.othersSection.hrScheduleApproval',
        path: '/schedules-and-shifts/hr-schedule-approval',
        type: 'item-header',
      }
    ],
  },
  {
    id: 'employment-compensations',
    translationKey: 'nav.employmentCompensations.main',
    type: 'dropdown',
    items: [
      {
        id: 'settings',
        translationKey: 'nav.employmentCompensations.settings.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'regulatory-documents',
            translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.main',
            type: 'item-dropdown',
            get requiredPermissions() {
              return getChildPermissions(this.items);
            },
            items: [
              {
                id: 'parameter-settings',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.parameterSettings',
                path: '/employment-compensations/settings/regulatory-documents/parameter-settings',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_PARAMS']
              },
              {
                id: 'work-experience-percentages',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.workExperiencePercentages',
                path: '/employment-compensations/settings/regulatory-documents/work-experience-percentages',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_WORK_EXP_TABLE']
              },
              {
                id: 'sick-leave-percentages',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.sickLeavePercentages',
                path: '/employment-compensations/settings/regulatory-documents/sick-leave-percentages',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_SICK_LEAVE_TABLE']
              },
              {
                id: 'child-benefits',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.childBenefits',
                path: '/employment-compensations/settings/regulatory-documents/child-benefits',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_CHILD_ALLOWANCE']
              },
              {
                id: 'income-tax',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.incomeTax',
                path: '/employment-compensations/settings/regulatory-documents/income-tax',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_DOD_TABLE']
              },
              {
                id: 'annual-tax',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.annualTax',
                path: '/employment-compensations/settings/regulatory-documents/annual-tax',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_ANNUAL_DOD_TABLE']
              },
              {
                id: 'child-tax-relief',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.childTaxRelief',
                path: '/employment-compensations/settings/regulatory-documents/child-tax-relief',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_TAX_RELIEF_CHILDREN']
              },
              {
                id: 'shift-schedule-controls',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.shiftScheduleControls',
                path: '/employment-compensations/settings/regulatory-documents/shift-schedule-controls',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_SHIFT_CTRL']
              },
              {
                id: 'work-allowances',
                translationKey: 'nav.employmentCompensations.settings.regulatoryDocuments.workAllowances',
                path: '/employment-compensations/settings/regulatory-documents/work-allowances',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_OVERTIME_ALLOWANCE']
              }
            ]
          },
          {
            id: 'statistical-forms',
            translationKey: 'nav.employmentCompensations.settings.statisticalForms',
            path: '/employment-compensations/settings/statistical-forms',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_STAT_FORMS']
          },
          {
            id: 'accrual-types',
            translationKey: 'nav.employmentCompensations.settings.accrualTypes',
            path: '/employment-compensations/settings/accrual-types',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_ACC_TYPES']
          },
          {
            id: 'deduction-types',
            translationKey: 'nav.employmentCompensations.settings.deductionTypes',
            path: '/employment-compensations/settings/deduction-types',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_DED_TYPES']
          },
          {
            id: 'working-time-calculation',
            translationKey: 'nav.employmentCompensations.settings.workingTimeCalculation',
            path: '/employment-compensations/settings/working-time-calculation',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_SETTINGS_WORK_TIME_CALC']
          }
        ],
      },
      {
        id: 'group-compensation-processing',
        translationKey: 'nav.employmentCompensations.groupCompensationProcessing.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'amount-accrual',
            translationKey: 'nav.employmentCompensations.groupCompensationProcessing.amountAccrual',
            path: '/employment-compensations/group-processing/amount-accrual',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_GROUP_PAY_CALC_SUMS']
          },
          {
            id: 'salary-percentage',
            translationKey: 'nav.employmentCompensations.groupCompensationProcessing.salaryPercentage',
            path: '/employment-compensations/group-processing/salary-percentage',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_GROUP_PAY_BASE_SALARY_PERCENTAGE']
          },
          {
            id: 'special-work-no-schedule',
            translationKey: 'nav.employmentCompensations.groupCompensationProcessing.specialWorkNoSchedule',
            path: '/employment-compensations/group-processing/special-work-no-schedule',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_GROUP_PAY_NIGHT_EXTRA_WORK']
          },
          {
            id: 'schedule-special-work',
            translationKey: 'nav.employmentCompensations.groupCompensationProcessing.scheduleSpecialWork',
            path: '/employment-compensations/group-processing/schedule-special-work',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_GROUP_PAY_WORK_SCHEDULE']
          }
        ],
      },
      {
        id: 'reports',
        translationKey: 'nav.employmentCompensations.reports.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'compensations',
            translationKey: 'nav.employmentCompensations.reports.compensations.main',
            type: 'item-dropdown',
            items: [
              {
                id: 'payslips',
                translationKey: 'nav.employmentCompensations.reports.compensations.payslips.main',
                type: 'item-dropdown',
                items: [
                  {
                    id: 'current-payslips',
                    translationKey: 'nav.employmentCompensations.reports.compensations.payslips.current',
                    path: '/employment-compensations/reports/compensations/payslips/current',
                    type: 'nested-item',
                    requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_PAYSLIPS']
                  },
                  {
                    id: 'payslips-archive',
                    translationKey: 'nav.employmentCompensations.reports.compensations.payslips.archive',
                    path: '/employment-compensations/reports/compensations/payslips/archive',
                    type: 'nested-item',
                    requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_PERM_ACCRUALS']
                  }
                ],
              },
              {
                id: 'visibility',
                translationKey: 'nav.employmentCompensations.reports.compensations.visibility',
                path: '/employment-compensations/reports/compensations/visibility',
                type: 'nested-item',
              },
              {
                id: 'payroll',
                translationKey: 'nav.employmentCompensations.reports.compensations.payroll',
                path: '/employment-compensations/reports/compensations/payroll',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_PAYROLL_STATEMENT']
              },
              {
                id: 'monthly-recap',
                translationKey: 'nav.employmentCompensations.reports.compensations.monthlyRecap',
                path: '/employment-compensations/reports/compensations/monthly-recap',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_MONTHLY_SUMMARY']
              },
              {
                id: 'bank-deductions',
                translationKey: 'nav.employmentCompensations.reports.compensations.bankDeductions',
                path: '/employment-compensations/reports/compensations/bank-deductions',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_BANK_DEDUCTIONS']
              },
              {
                id: 'overpayments',
                translationKey: 'nav.employmentCompensations.reports.compensations.overpayments',
                path: '/employment-compensations/reports/compensations/overpayments',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_OVERPAID_RECOVERY']
              },
              {
                id: 'income-tax',
                translationKey: 'nav.employmentCompensations.reports.compensations.incomeTax',
                path: '/employment-compensations/reports/compensations/income-tax',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_DOD_TRANSFER']
              },
              {
                id: 'advance-payroll',
                translationKey: 'nav.employmentCompensations.reports.compensations.advancePayroll',
                path: '/employment-compensations/reports/compensations/advance-payroll',
                type: 'nested-item',
                requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_ADV_PAYROLL']
              }
            ],
          },
          {
            id: 'custom-reports',
            translationKey: 'nav.employmentCompensations.reports.customReports',
            path: '/employment-compensations/reports/custom-reports',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_USER_REPORTS']
          },
          {
            id: 'statistical-reports',
            translationKey: 'nav.employmentCompensations.reports.statisticalReports',
            path: '/employment-compensations/reports/statistical-reports',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_STAT_REPORTS']
          },
          {
            id: 'management-salary-report',
            translationKey: 'nav.employmentCompensations.reports.managementSalaryReport',
            path: '/employment-compensations/reports/management-salary-report',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_REPORTS_PROJECT_EXPENSES']
          }
        ],
      },
      {
        id: 'others-section',
        translationKey: 'nav.employmentCompensations.othersSection.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'employee-compensation-processing',
            translationKey: 'nav.employmentCompensations.othersSection.employeeCompensationProcessing',
            path: '/employment-compensations/employee-processing',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_PAY_PROC_BY_EMPLOYEES']
          },
          {
            id: 'period-management',
            translationKey: 'nav.employmentCompensations.othersSection.periodManagement',
            path: '/employment-compensations/period-management',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_GEN_RECALC_OPEN_FIX_PERIODS']
          },
          {
            id: 'annual-tax-adjustment',
            translationKey: 'nav.employmentCompensations.othersSection.annualTaxAdjustment',
            path: '/employment-compensations/annual-tax-adjustment',
            type: 'item',
            requiredPermissions: ['COMP_EMP_CONTRACTS_ANNUAL_TAX_EQUALIZATION']
          }
        ],
      }
    ],
  },
  {
    id: 'payments',
    translationKey: 'nav.payments.main',
    type: 'dropdown',
    get requiredPermissions() {
      return getChildPermissions(this.items);
    },
    items: [
      {
        id: 'settings',
        translationKey: 'nav.payments.settings.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'accounting-operations',
            translationKey: 'nav.payments.settings.accountingOperations',
            path: '/payments/settings/accounting-operations',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_ACC_OPERATIONS']
          },
          {
            id: 'accounting-templates-replication',
            translationKey: 'nav.payments.settings.accountingTemplatesReplication',
            path: '/payments/settings/accounting-templates-replication',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_REPLICATE_ACC_TEMPLATES']
          },
          {
            id: 'payment-document-templates',
            translationKey: 'nav.payments.settings.paymentDocumentTemplates',
            path: '/payments/settings/payment-document-templates',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_PAYMENT_DOC_TEMPLATES']
          },
          {
            id: 'min-insurance-income',
            translationKey: 'nav.payments.settings.minInsuranceIncome',
            path: '/payments/settings/min-insurance-income',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_MIN_SOCIAL_SEC_INCOME_CODES']
          },
          {
            id: 'banks',
            translationKey: 'nav.payments.settings.banks',
            path: '/payments/settings/banks',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_BANKS_BRANCHES']
          },
          {
            id: 'company-bank-accounts',
            translationKey: 'nav.payments.settings.companyBankAccounts',
            path: '/payments/settings/company-bank-accounts',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_COMPANY_ACCOUNTS']
          },
          {
            id: 'currencies',
            translationKey: 'nav.payments.settings.currencies',
            path: '/payments/settings/currencies',
            type: 'item',
            requiredPermissions: ['PAYMENTS_SETTINGS_CURRENCIES']
          }
        ],
      },
      {
        id: 'accounting',
        translationKey: 'nav.payments.accounting.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'generate-operations',
            translationKey: 'nav.payments.accounting.generateOperations',
            path: '/payments/accounting/generate-operations',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_GEN_OPERATIONS']
          },
          {
            id: 'settings-control',
            translationKey: 'nav.payments.accounting.settingsControl',
            path: '/payments/accounting/settings-control',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_OPERATIONS_CTRL']
          },
          {
            id: 'accounting-documents',
            translationKey: 'nav.payments.accounting.accountingDocuments',
            path: '/payments/accounting/documents',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_ACC_DOCS']
          },
          {
            id: 'chronological-journals',
            translationKey: 'nav.payments.accounting.chronologicalJournals',
            path: '/payments/accounting/chronological-journals',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_CHRONO_JOURNALS']
          },
          {
            id: 'payment-documents',
            translationKey: 'nav.payments.accounting.paymentDocuments',
            path: '/payments/accounting/payment-documents',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_PAYMENT_DOCS']
          },
          {
            id: 'mass-payment-export',
            translationKey: 'nav.payments.accounting.massPaymentExport',
            path: '/payments/accounting/mass-payment-export',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_EXPORT_MASS_PAYMENTS']
          },
          {
            id: 'chart-of-accounts',
            translationKey: 'nav.payments.accounting.chartOfAccounts',
            path: '/payments/accounting/chart-of-accounts',
            type: 'item',
            requiredPermissions: ['PAYMENTS_ACCOUNTING_COA']
          }
        ],
      },
      {
        id: 'references',
        translationKey: 'nav.payments.references.main',
        type: 'nested-dropdown',
        items: [
          {
            id: 'insurances',
            translationKey: 'nav.payments.references.insurances',
            path: '/payments/references/insurances',
            type: 'item',
          },
          {
            id: 'data-for-e.g',
            translationKey: 'nav.payments.references.dataForE.g',
            path: '/payments/references/data-for-e.g',
            type: 'item',
          }
        ]
      },
      {
        id: 'others-section',
        translationKey: 'nav.payments.othersSection.main',
        type: 'nested-dropdown',
        get requiredPermissions() {
          return getChildPermissions(this.items);
        },
        items: [
          {
            id: 'nap-insured-persons',
            translationKey: 'nav.payments.othersSection.napInsuredPersons',
            path: '/payments/nap-insured-persons',
            type: 'item',
            requiredPermissions: ['PAYMENTS_NAP_INSURED_PERSONS']
          },
          {
            id: 'e-compensations',
            translationKey: 'nav.payments.othersSection.eCompensations',
            path: '/payments/e-compensations',
            type: 'item',
            requiredPermissions: ['PAYMENTS_CASH_BENEFITS']
          },
          {
            id: 'mass-payments',
            translationKey: 'nav.payments.othersSection.massPayments',
            path: '/payments/mass-payments',
            type: 'item',
            requiredPermissions: ['PAYMENTS_MASS_PAYMENTS']
          },
          {
            id: 'file-import',
            translationKey: 'nav.payments.othersSection.fileImport',
            path: '/payments/file-import',
            type: 'item',
            requiredPermissions: ['PAYMENTS_IMPORT_FROM_FILE']
          }
        ],
      }
    ],
  },
  {
    id: 'fees',
    translationKey: 'nav.fees.main',
    type: 'dropdown',
    get requiredPermissions() {
      return getChildPermissions(this.items);
    },
    items: [
      {
        id: 'fee-contracts-register',
        translationKey: 'nav.fees.feeContractsRegister',
        path: '/fees/fee-contracts-register',
        type: 'item-header',
        requiredPermissions: ['HONORARIUMS_CONTRACTED_PERSONS_REGISTER']
      },
      {
        id: 'civil-contracts-interns',
        translationKey: 'nav.fees.civilContractsInterns',
        path: '/fees/civil-contracts-interns',
        type: 'item-header',
        requiredPermissions: ['CIVIL_CONTRACTS_INTERNS']
      }

    ]
  },
  {
    id: 'training',
    translationKey: 'nav.training.main',
    type: 'dropdown',
    get requiredPermissions() {
      return getChildPermissions(this.items);
    },
    items: [
      {
        id: 'settings',
        translationKey: 'nav.training.settings',
        path: '/training/settings',
        type: 'item-header',
        requiredPermissions: ['TRAINING_SECTION_SETTINGS']
      },
      {
        id: 'participants',
        translationKey: 'nav.training.participants',
        path: '/training/participants',
        type: 'item-header',
        requiredPermissions: ['TRAINING_PARTICIPANTS']
      },
      {
        id: 'reports',
        translationKey: 'nav.training.reports',
        path: '/training/reports',
        type: 'item-header',
        requiredPermissions: ['TRAINING_REPORTS_MODULE']
      }
    ]
  },
  {
    id: 'additional-settings',
    translationKey: 'nav.additionalSettings.main',
    type: 'dropdown',
    items: [
      {
        id: 'additional-work-rights',
        translationKey: 'nav.additionalSettings.additionalWorkRights',
        path: '/additional-settings/additional-work-rights',
        type: 'item-header',
        requiredPermissions: ['SETTINGS_RIGHTS_FOR_WORK']
      },
      {
        id: 'profile-activation',
        translationKey: 'nav.additionalSettings.profileActivation',
        path: '/additional-settings/profile-activation',
        type: 'item-header',
      },
      {
        id: 'calendar-holidays',
        translationKey: 'nav.additionalSettings.calendarHolidays',
        path: '/additional-settings/calendar-holidays',
        type: 'item-header',
        requiredPermissions: ['SETTINGS_CALENDAR_HOLIDAYS']
      },
      {
        id: 'nomenclature-translations',
        translationKey: 'nav.additionalSettings.nomenclatureTranslations',
        path: '/additional-settings/nomenclature-translations',
        type: 'item-header',
        requiredPermissions: ['SETTINGS_NOMENCLATURES_TRANS']
      },
      {
        id: 'nomenclatures',
        translationKey: 'nav.additionalSettings.nomenclatures',
        path: '/additional-settings/nomenclatures',
        type: 'item-header',
        requiredPermissions: ['SETTINGS_NOMENCLATURES']
      },
      {
        id: 'regions',
        translationKey: 'nav.additionalSettings.regions',
        path: '/additional-settings/regions',
        type: 'item-header',
        requiredPermissions: ['SETTINGS_AREAS_MUNICIPALITIES']
      },
      {
        id: 'template-settings',
        translationKey: 'nav.administrator.templateDocuments.templateSettings',
        path: '/additional-settings/template-settings',
        type: 'item-header',
        requiresEditReportTemplates: true
      }
    ]
  },
  {
    id: 'administrator',
    translationKey: 'nav.administrator.main',
    type: 'dropdown',
    items: [

      {
        id: 'users',
        translationKey: 'nav.administrator.adminSection.users',
        path: '/administrator/users',
        type: 'item-header'
      },
      {
        id: 'user-groups',
        translationKey: 'nav.administrator.adminSection.userGroups',
        path: '/administrator/user-groups',
        type: 'item-header'
      }
    ]
  },
  {
    id: 'logout',
    translationKey: 'nav.logout',
    path: '/signout',
    type: 'item',
  }
]; 